import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { AppComponent } from '../../app.component';
import { BaseService } from '../../shared/services/base.service';

import { Customer } from '../../shared/models/customer.interface';
import { CustomerNote } from '../../shared/models/customer.note.interface';
import { Contact } from '../../shared/models/contact.interface';
import { ContactType } from '../../shared/models/contact.type.interface';
import { CustomerType } from '../../shared/models/customer.type.interface';
import { Office } from '../../shared/models/office.interface';
import { LeadSourceType } from '../../shared/models/lead.source.type.interface';
import { PaymentType } from '../../shared/models/payment.type.interface';
import { PaymentTerm } from '../../shared/models/payment.term.interface';
import { User } from '../../shared/models/user.interface';
import { CustomerFilter } from '../models/customer.filter';
import { JobType } from '../../shared/models/job.type.interface';
import { DealerCustomerSummary } from '../list/list.component';
import { CustomerImport } from '../../shared/models/customer.import.interface';
import { FileUpload } from '../../shared/models/file.upload.interface';
import { CustomerImportSummary } from '../import/import.component';
import { Router } from '@angular/router';

@Injectable()

export class CustomerService extends BaseService {

  constructor(private appComponent: AppComponent, protected http: HttpClient, router: Router) {
    super(http, router);
  }

  getCustomer(customerId): Observable<Customer> {
    return this.httpGet<Customer>("/customer/" + customerId);
  }

  getSummary(filter: CustomerFilter): Observable<DealerCustomerSummary> {
    let params = new HttpParams();

    if (filter.customerName) params = params.append('customerName', filter.customerName);
    if (filter.customerType) params = params.append('customerType', filter.customerType.toString());
    if (filter.salespersonId) params = params.append('salespersonId', filter.salespersonId.toString());
    if (filter.showInactive) params = params.append('showInactive', filter.showInactive.toString());

    return this.httpGet<DealerCustomerSummary>("/customer/summary?" + params.toString());
  }

  getList(filter: CustomerFilter, sortColumn: string, sortDirection: string, page: number): Observable<Customer[]> {
    let params = new HttpParams();

    if (page != null) params = params.append('page', page.toString());
    if (sortColumn != null) params = params.append('sortColumn', sortColumn);
    if (sortDirection != null) params = params.append('sortDirection', sortDirection);
    if (filter.customerName) params = params.append('customerName', filter.customerName);
    if (filter.customerType) params = params.append('customerType', filter.customerType.toString());
    if (filter.salespersonId) params = params.append('salespersonId', filter.salespersonId.toString());
    if (filter.showInactive) params = params.append('showInactive', filter.showInactive.toString());

    return this.httpGet<Customer[]>("/customer?" + params.toString());
  }

  getImportSummary(filter: CustomerFilter): Observable<CustomerImportSummary> {
    let params = new HttpParams();

    return this.httpGet<CustomerImportSummary>("/customer/importSummary?" + params.toString());
  }

  getImportList(filter: CustomerFilter, sortColumn: string, sortDirection: string, page: number): Observable<CustomerImport[]> {
    let params = new HttpParams();

    if (page != null) params = params.append('page', page.toString());
    if (sortColumn != null) params = params.append('sortColumn', sortColumn);
    if (sortDirection != null) params = params.append('sortDirection', sortDirection);
    if (filter.customerName) params = params.append('customerName', filter.customerName);
    if (filter.customerType) params = params.append('customerType', filter.customerType.toString());
    if (filter.salespersonId) params = params.append('salespersonId', filter.salespersonId.toString());
    if (filter.showInactive) params = params.append('showInactive', filter.showInactive.toString());

    return this.httpGet<CustomerImport[]>("/customer/import?" + params.toString());
  }

  uploadCustomerImport(importFile: FileUpload): Observable<number> {
    return this.httpPost<number>("/customer/import", importFile);
  }

  deleteCustomerImport(customerImportId: number): Observable<number> {
    return this.httpDelete<number>("/customer/import/" + customerImportId);
  }

  updateCustomerImport(customerImportId: number, customerImport: CustomerImport): Observable<number> {
    return this.httpPut<number>("/customer/import/" + customerImportId, customerImport);
  }

  submitCustomerImport(accept: boolean): Observable<number> {
    return this.httpPost<number>("/customer/import/" + accept, {});
  }

  getContact(contactId: number): Observable<Contact> {
    return this.httpGet<Contact>("/customer/contact/" + contactId);
  }

  getNote(noteId: number): Observable<CustomerNote> {
    return this.httpGet<CustomerNote>("/customer/note/" + noteId);
  }

  deleteNote(noteId: number): Observable<number> {
    return this.httpDelete<number>("/customer/note/" + noteId);
  }

  getNoteAttachment(noteId: number): Observable<Blob> {
    return this.httpGetFile("/customer/note/" + noteId + "/attachment");
  }

  getContactTypes(): Observable<ContactType[]> {
    return this.httpGet<ContactType[]>("/customer/contactType");
  }

  getCustomerTypes(): Observable<CustomerType[]> {
    return this.httpGet<CustomerType[]>("/customer/customerType");
  }

  getJobTypes(): Observable<JobType[]> {
    return this.httpGet<JobType[]>("/quote/jobType");
  }

  getSalespersons(activeOnly?: boolean): Observable<User[]> {
    if (activeOnly != null) {
      return this.httpGet<User[]>("/user/salesperson?activeOnly=" + activeOnly );
    }
    else {
      return this.httpGet<User[]>("/user/salesperson" );
    }
  }

  getOffices(): Observable<Office[]> {
    return this.httpGet<Office[]>("/office");
  }

  getStates(): Observable<string[]> {
    return this.httpGet<string[]>("/admin/states");
  }

  getLeadSourceTypes(): Observable<LeadSourceType[]> {
    return this.httpGet<LeadSourceType[]>("/customer/leadSource");
  }

  getPaymentTypes(): Observable<PaymentType[]> {
    return this.httpGet<PaymentType[]>("/customer/paymentType");
  }

  getPaymentTerms(): Observable<PaymentTerm[]> {
    return this.httpGet<PaymentTerm[]>("/customer/paymentTerm");
  }

  deleteCustomer(customerId: number): Observable<number> {
    return this.httpDelete<number>("/customer/" + customerId);
  }

  activateCustomer(customerId: number): Observable<number> {
    return this.httpPut<number>("/customer/" + customerId, null);
  }

  saveCustomer(customer: Customer): Observable<number> {
    if (customer.customer_ID == null)
      return this.httpPut<number>("/customer/", customer);
    else
      return this.httpPost<number>("/customer/" + customer.customer_ID, customer);
  }

  setContactStatus(contactId: number, isActive: boolean): Observable<number> {
    return this.httpPost<number>("/customer/contactStatus/" + contactId + '?active=' + isActive, {});
  }

  setPrimaryContact(customerId: number, contactId: number): Observable<number> {
    return this.httpPost<number>("/customer/primaryContact/" + customerId + '?contactId=' + contactId, {});
  }

  saveContact(contact: Contact): Observable<number> {
    if (contact.contact_ID == null)
      return this.httpPut<number>("/customer/contact", contact);
    else
      return this.httpPost<number>("/customer/contact/" + contact.contact_ID, contact);
  }

  sendSMSOptIn(contact: Contact): Observable<any> {
    return this.httpPost<any>(`/SMS/sendSMSOptIn/${contact.contact_ID}`, contact.contact_Mobile);
  }

  saveNote(note: CustomerNote): Observable<number> {
    if (note.note_ID == null)
      return this.httpPut<number>("/customer/note", note);
    else
      return this.httpPost<number>("/customer/note/" + note.note_ID, note);
  }
}
